/**
 * ChatbotAPI.js
 * 
 * Service for communicating with the Python conversational RAG chatbot backend.
 * Integrates with conversational_rag_chatbot.py
 */

import { getEnvVariable } from '../../modules/environmentalList';

class ChatbotAPI {
  constructor() {
    // Configuration for Python backend - use AIChat from env.json
    this.baseUrl = getEnvVariable('AIChat') || process.env.REACT_APP_CHATBOT_API_URL || 'http://localhost:8000';
    this.timeout = 30000; // 30 seconds
    
    // Generate a unique session ID for this chat instance
    this.sessionId = this._generateSessionId();
    
    console.log('ChatbotAPI initialized with baseUrl:', this.baseUrl);
  }
  
  /**
   * Generate a unique session ID
   */
  _generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
  }
  
  /**
   * Send message to the Python RAG chatbot backend
   */
  async sendMessage(text) {
    try {
      console.log('Sending message to Python backend:', text);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);
      
      // Prepare the request payload
      const payload = {
        message: text,
        session_id: this.sessionId
      };
      
      // Send request to Python backend
      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      
      // Clear timeout
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('Received response from Python backend:', responseData);
      
      // Return the response text
      return responseData.response || responseData.message || 'Sorry, I didn\'t receive a proper response.';
      
    } catch (error) {
      console.error('Error communicating with Python backend:', error);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      
      // Check if it's a network error (backend not running)
      if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
        throw new Error('Unable to connect to the chatbot service. Please make sure the Python backend is running.');
      }
      
      throw new Error('Failed to get response from chatbot. Please try again.');
    }
  }
  
  /**
   * Check if the Python backend is available
   */
  async checkHealth() {
    try {
      console.log('Checking health at:', `${this.baseUrl}/health`);
      
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        timeout: 5000
      });
      
      const isHealthy = response.ok;
      console.log('Health check result:', isHealthy);
      return isHealthy;
      
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
  
  /**
   * Get chat history for the current session
   */
  async getChatHistory() {
    try {
      const response = await fetch(`${this.baseUrl}/history/${this.sessionId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.history || [];
      
    } catch (error) {
      console.error('Error getting chat history:', error);
      return [];
    }
  }
  
  /**
   * Clear chat history for the current session
   */
  async clearHistory() {
    try {
      const response = await fetch(`${this.baseUrl}/clear/${this.sessionId}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      return response.ok;
      
    } catch (error) {
      console.error('Error clearing chat history:', error);
      return false;
    }
  }
}

// Create a singleton instance
const chatbotAPI = new ChatbotAPI();

export default chatbotAPI; 